import React, { useRef, useEffect } from 'react';
import { isFirefox } from 'react-device-detect';
import sanitizeHtml from 'sanitize-html';
import _omit from 'lodash/omit';
import _merge from 'lodash/merge';
import { SPACING_4 } from '@nokia-csf-uxr/freeform-design-tokens/tokens/spacing';
import { getContext, useCombinedRefs } from 'common';
import {
  TextwChipsEditableWrapper,
  TextwChipsList,
} from 'components/TextwChips';

import { MenuProps } from 'components/Menu';

import Styled from './styles';

const SANATIZE_CONF = {
  allowedTags: [],
  allowedAttributes: {}
};

const EMPTY_STRING = '';

const resolveTextInputConfig = (config) => {
  const divWidth = config.empty ? 'calc(100% - 1rem)' : 'auto';
  return {
    divStyle: {
      width: divWidth,
      maxWidth: config.empty && !config.lastInput ? '0.3125rem' : 'none',
      margin: config.RTL ? `0 0 0 ${SPACING_4}` : `0 ${SPACING_4} 0 0`,
    },
    style: {
      display: config.empty && !isFirefox ? 'inline-block' : 'inline',
      width: !config.empty ? undefined : `${config.containerWidth - config.lastFieldEndPosition - 32}px`, // 32px for padding
      maxWidth: config.empty && !config.lastInput ? '0.3125rem' : 'none',
      lineHeight: config.empty ? '1.25' : undefined, // to make cursor position in empty field correct
      border: 'none',
      boxShadow: 'none',
    },
  };
};

export interface TextwChipsContentEditableProps extends Omit<React.ComponentPropsWithRef<'div'>, 'onChange'> {
  /** The id of the item. */
  id: string,
  /** The value of the field. */
  value?: string;
  /** The short hint displayed in the input when it is empty. Note: The native input element's `placeholder` attribute will be set using this prop. */
  placeholder?: string;
  /** If `true`, the component will be disabled. */
  disabled?: boolean;
  /** Set `true`, if the field is the last one in its container. */
  lastInput?: boolean;
  /** The width of the container. Used to calculate the input width in multiline mode. */
  containerWidth?: number;
  /** The ending position of the preceding field. Used to calculate the input width in multiline mode. */
  lastFieldEndPosition?: number;
  /** The tab index. */
  tabIndex?: number;
  /** Properties for TextwChipsList. */
  listProps?: {
    /** Override `Menu` positioning options based on popper (https://popper.js.org/docs/v2/). */
    menuProps?: Partial<MenuProps>;
    /** Object of list items and the event triggering the List. */
    contextMenu?: {
      event?: {},
      items: {
        label?: string,
        itemNode?: React.ReactNode,
        key?: string,
        content?: React.ReactNode,
        searchPattern?: string,
        onClick: React.MouseEventHandler,
        submitChip?: boolean
      }[]
    },
    /** The element reference that triggered the menu. */
    triggerEl?: React.RefObject<HTMLElement>;
    /** Reference to pass to the List. */
    listRef?: React.RefObject<HTMLElement>;
  };
  /** Set `true`, if the field is the first item. */
  firstInput?: boolean;
  /** The variant of the field. */
  variant?: 'outlined' | 'underlined';
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** Callback fired when the value is changed. */
  onChange?: (arg: string) => void;
  /** @ignore */
  onKeyDown?: React.KeyboardEventHandler;
  /** @ignore */
  onFocus?: React.FocusEventHandler;
  /** @ignore */
  onBlur?: React.FocusEventHandler;
}

/** Internal component. Any other props than the `TextwChipsEditElement` props will be provided to the `ContentEditable` component. */
const TextwChipsContentEditable = React.forwardRef<HTMLElement, TextwChipsContentEditableProps>((props: TextwChipsContentEditableProps, ref) => getContext(({ RTL, theme: globalTheme }) => {
  const {
    id,
    value,
    placeholder,
    disabled,
    lastInput,
    firstInput,
    containerWidth,
    lastFieldEndPosition,
    tabIndex,
    onChange,
    onKeyDown,
    onFocus,
    onBlur,
    listProps,
    variant,
    theme: propTheme,
    ...otherProps
  } = props;
  const {
    contextMenu,
    triggerEl,
    listRef
  } = listProps || {};
  const text = useRef<string>(((value === EMPTY_STRING) && placeholder) ? placeholder : value);
  const innerRefEl = useRef(null);
  const combinedRefs = useCombinedRefs(innerRefEl, ref);
  const isPlaceholder = !!((value === EMPTY_STRING) && placeholder);

  // move cursor to end of text
  const positionCursorAtEnd = () => {
    if (disabled) return;
    if (!(innerRefEl && innerRefEl.current)) return;
    const textNode = innerRefEl.current.firstChild;
    if (!textNode) return;
    const endPosition = textNode.length;
    if (!document.createRange) return;
    const range = document.createRange();
    range.setStart(textNode, endPosition);
    range.setEnd(textNode, endPosition);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  };

  // move cursor to beginning of text
  const positionCursorAtBeginning = () => {
    if (disabled) return;
    if (!(innerRefEl && innerRefEl.current)) return;
    const textNode = innerRefEl.current.firstChild;
    if (!textNode) return;
    if (!document.createRange) return;
    const range = document.createRange();
    range.setStart(textNode, 0);
    range.setEnd(textNode, 0);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  };

  useEffect(() => {
    text.current = isPlaceholder ? placeholder : value;
  }, [value]);

  const handleKeyDown = (event) => {
    onKeyDown && onKeyDown(event);
  };

  const handleOnChange = (event) => {
    // remove any html tags or attributes from entered text
    const sanitizedText = sanitizeHtml(event.target.value, SANATIZE_CONF);
    const newText = placeholder ? sanitizedText.replace(placeholder, '') : sanitizedText;
    text.current = newText;
    onChange && onChange(sanitizedText);
  };

  const handleContentEditableOnFocus = () => {
    if (isPlaceholder) {
      positionCursorAtBeginning();
    } else {
      text.current !== EMPTY_STRING && positionCursorAtEnd();
    }
  };

  const handleContentEditableOnBlur = () => {
    onChange(text.current);
  };

  // to prevent editing in the placeholder (to simulate the textarea handling of placeholder text)
  // in case the mouse is put in the middle of the placeholder text
  const handleMouseUp = () => {
    if (isPlaceholder) {
      positionCursorAtBeginning();
    }
  };
  // to prevent editing in the placeholder (to simulate the textarea handling of placeholder text)
  const handleMouseDown = (event) => {
    if (isPlaceholder) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const resolvedProps = resolveTextInputConfig({
    lastInput,
    containerWidth,
    lastFieldEndPosition,
    value,
    empty: value === EMPTY_STRING,
    RTL,
  });
  const resolvedTheme = _merge({}, variant === 'outlined' ?
    globalTheme.TEXT_INPUT.OUTLINED :
    globalTheme.TEXT_INPUT.UNDERLINED, propTheme);
  return (
    <Styled.EditableWrapper
      id={id}
      style={resolvedProps.divStyle}
      disabled={disabled}
      theme={resolvedTheme}
      $isplaceholder={value === EMPTY_STRING && placeholder}
      {..._omit(otherProps, ['listFocused', 'listProps', 'autocomplete'])}
      onFocus={(e) => { onFocus && onFocus(e); }}
      onBlur={(e) => { onBlur && onBlur(e); }}
    >
      <TextwChipsEditableWrapper
        tabIndex={tabIndex}
        role="textbox"
        ref={combinedRefs}
        html={firstInput ? value : text.current}
        tagName="span"
        style={resolvedProps.style}
        disabled={disabled}
        onChange={handleOnChange}
        onKeyDown={handleKeyDown}
        onFocus={handleContentEditableOnFocus}
        onBlur={handleContentEditableOnBlur}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
      />
      <TextwChipsList
        {...{
          list: contextMenu,
          triggerEl,
          listRef
        }}
      />
    </Styled.EditableWrapper>
  );
}));

TextwChipsContentEditable.defaultProps = {
  value: EMPTY_STRING,
  placeholder: undefined,
  disabled: false,
  lastInput: false,
  containerWidth: 0,
  lastFieldEndPosition: 0,
  tabIndex: undefined,
  listProps: undefined,
  onChange: undefined,
  onKeyDown: undefined,
  onFocus: undefined,
  onBlur: undefined,
  firstInput: false,
  variant: 'outlined',
  theme: {}
};

TextwChipsContentEditable.displayName = 'TextwChipsContentEditable';
export default TextwChipsContentEditable;
